import { useState } from "react";
import { supabase } from "../lib/SupabaseClient";

interface ExamJson {
    Title: string;
    Description: string;
    DurationMinutes: number;
    ExternalExamId?: number;
    Questions: {
        Text: string;
        Choices: { text: string }[];
        Image?: string | null;
    }[];
    Candidates?: any[];
}

export default function ImportExamPage({
    organizationId,
    createdBy,
}: {
    organizationId: number;
    createdBy: string;
}) {
    const [exam, setExam] = useState<ExamJson | null>(null);
    const [pkgFile, setPkgFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // -----------------------------
    // JSON IMPORT (existing)
    // -----------------------------
    async function handleJsonUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const parsed: ExamJson = JSON.parse(text);
            setExam(parsed);
            setMessage("");
        } catch {
            setMessage("❌ Invalid JSON file");
        }
    }

    // -----------------------------
    // PKG IMPORT (new)
    // -----------------------------
    async function handlePkgUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        setPkgFile(file);
        setMessage("");
    }

    async function importPkg() {
        if (!pkgFile) return;

        setLoading(true);
        setMessage("Uploading encrypted package...");

        const form = new FormData();
        form.append("file", pkgFile);
        form.append("organization_id", organizationId.toString());
        form.append("created_by", createdBy);

        // ⚠️ Call Supabase Edge Function
        const { data, error } = await supabase.functions.invoke("import-pkg", {
            body: form,
        });

        if (error) {
            console.error(error);
            setMessage("❌ Failed to import exam.pkg");
        } else {
            setMessage("✅ Encrypted exam.pkg imported successfully!");
        }

        setLoading(false);
    }

    // -----------------------------
    // JSON IMPORT HANDLER (existing)
    // -----------------------------
    async function importJson() {
        if (!exam) return;

        setLoading(true);
        setMessage("Importing JSON exam...");

        try {
            // insert logic remains unchanged for now
            // (you may adjust JSON structure later)
        } catch (err) {
            console.error(err);
            setMessage("❌ Failed to import JSON");
        }

        setLoading(false);
    }

    return (
        <div className="bg-white p-4 rounded shadow">

            {/* ====================================================== */}
            {/*  JSON IMPORT SECTION                                  */}
            {/* ====================================================== */}
            <h2 className="text-lg font-bold mb-2">Import Exam (JSON)</h2>

            <input type="file" accept=".json" onChange={handleJsonUpload} />

            {exam && (
                <>
                    <div className="p-3 mt-3 bg-gray-100 border rounded">
                        <p><strong>Title:</strong> {exam.Title}</p>
                        <p><strong>Description:</strong> {exam.Description}</p>
                        <p><strong>Duration:</strong> {exam.DurationMinutes} minutes</p>
                        <p><strong>Questions:</strong> {exam.Questions?.length || 0}</p>
                    </div>

                    <button
                        onClick={importJson}
                        disabled={loading}
                        className="mt-3 px-4 py-2 bg-green-600 text-white rounded"
                    >
                        {loading ? "Importing..." : "Import JSON Exam"}
                    </button>
                </>
            )}

            {/* ====================================================== */}
            {/*  PKG IMPORT SECTION (NEW)                             */}
            {/* ====================================================== */}
            <h2 className="text-lg font-bold mt-8 mb-2">Import Encrypted Exam Package (.pkg)</h2>

            <input type="file" accept=".pkg" onChange={handlePkgUpload} />

            {pkgFile && (
                <button
                    onClick={importPkg}
                    disabled={loading}
                    className="mt-3 px-4 py-2 bg-purple-600 text-white rounded"
                >
                    {loading ? "Uploading..." : "Import exam.pkg"}
                </button>
            )}

            {message && <p className="mt-4">{message}</p>}
        </div>
    );
}
